
import { countryMapping } from './CountryMapping';
import { regionMapping } from './RegionMapping';
import { cityMapping } from './CityMapping';

export const locationMapping = {
  countries: countryMapping,
  regions: regionMapping,
  cities: cityMapping
};

export interface DetectedLocation {
  type: 'country' | 'region' | 'city';
  info: any;
  priority: number; // Higher number = higher priority
}

export const detectLocation = (searchTerms: string[]): DetectedLocation | null => {
  console.log('=== LOCATION DETECTION WITH HIERARCHY ===');
  console.log('Original search terms:', searchTerms);
  
  let detectedLocations: DetectedLocation[] = [];
  
  // Check for cities first (highest priority)
  for (const [term, info] of Object.entries(locationMapping.cities)) {
    const termLower = term.toLowerCase();
    const matched = searchTerms.some(searchTerm => {
      const searchLower = searchTerm.toLowerCase();
      return searchLower === termLower || 
             searchLower.includes(termLower) || 
             termLower.includes(searchLower);
    });
    
    if (matched) {
      console.log('✅ DETECTED CITY:', term, '→', info);
      detectedLocations.push({ type: 'city', info, priority: 3 });
    }
  }
  
  // Check for regions (medium priority)
  for (const [term, info] of Object.entries(locationMapping.regions)) {
    const termLower = term.toLowerCase();
    const matched = searchTerms.some(searchTerm => {
      const searchLower = searchTerm.toLowerCase();
      
      if (searchLower === termLower) return true;
      if (termLower.includes('garda') && searchLower.includes('garda')) return true;
      if (termLower.includes('gardameer') && searchLower.includes('gardameer')) return true;
      if (searchLower.includes(termLower) || termLower.includes(searchLower)) return true;
      
      return false;
    });
    
    if (matched) {
      console.log('✅ DETECTED REGION:', term, '→', info);
      detectedLocations.push({ type: 'region', info, priority: 2 });
    }
  }
  
  // Check for countries (lowest priority)
  console.log('=== CHECKING COUNTRIES ===');
  for (const [term, country] of Object.entries(locationMapping.countries)) {
    const termLower = term.toLowerCase();
    console.log(`🔍 Checking country mapping: "${term}" (${termLower}) → "${country}"`);
    
    const matched = searchTerms.some(searchTerm => {
      const searchLower = searchTerm.toLowerCase();
      
      console.log(`  - Comparing search term "${searchLower}" with country term "${termLower}"`);
      
      // Exact match
      if (searchLower === termLower) {
        console.log('    ✅ EXACT MATCH!');
        return true;
      }
      
      // Partial match - search term contains country term
      if (searchLower.includes(termLower)) {
        console.log('    ✅ SEARCH CONTAINS COUNTRY TERM!');
        return true;
      }
      
      // Partial match - country term contains search term  
      if (termLower.includes(searchLower)) {
        console.log('    ✅ COUNTRY TERM CONTAINS SEARCH!');
        return true;
      }
      
      console.log('    ❌ No match');
      return false;
    });
    
    if (matched) {
      console.log(`🎯 DETECTED COUNTRY: "${term}" → "${country}"`);
      detectedLocations.push({ type: 'country', info: { country }, priority: 1 });
    }
  }
  
  // IMPROVED LOGIC: Smart priority selection for region searches
  if (detectedLocations.length > 0) {
    detectedLocations.sort((a, b) => b.priority - a.priority);
    
    // Special case: If we detect both region and city, and the region name appears explicitly in search terms
    const regionDetected = detectedLocations.find(loc => loc.type === 'region');
    const cityDetected = detectedLocations.find(loc => loc.type === 'city');
    
    if (regionDetected && cityDetected) {
      // Check if region name appears explicitly in the search terms
      const regionName = regionDetected.info.region.toLowerCase();
      const searchString = searchTerms.join(' ').toLowerCase();
      
      // For "Costa Brava" specifically, or any region that appears in the search
      if (searchString.includes(regionName) || searchString.includes(regionName.replace(' ', ''))) {
        console.log('🎯 REGION NAME EXPLICITLY MENTIONED - PRIORITIZING REGION OVER CITY');
        console.log('Selected region:', regionDetected);
        console.log('Skipped city:', cityDetected);
        return regionDetected;
      }
    }
    
    const selectedLocation = detectedLocations[0];
    
    console.log('🏆 SELECTED LOCATION (HIGHEST PRIORITY):', selectedLocation);
    console.log('📍 Other detected locations (ignored):', detectedLocations.slice(1));
    
    return selectedLocation;
  }
  
  console.log('❌ NO LOCATION DETECTED from terms:', searchTerms);
  return null;
};
